import { msg } from "@omegagrid/localize";
import { MenuItem, dates } from "@omegagrid/core";
import { NodeModel, NodeSource, TreeOptions, TreeSource } from "@omegagrid/tree";
import { File } from "@reporting/rp-types/etl";
import { html, render, css } from "lit";
import rp, { BrowserAdapter, formatBytes, formatSeconds } from "@reporting/rp-app-host";
import { styleMap } from "lit/directives/style-map.js";
import { downloadEtlFile, uploadEtlFile } from "./fileActions";

const formatDate = (d: string) => dates.format(new Date(d), dates.getDateFormat() + ' HH:mm');

function sortFiles(files: NodeSource[]) {
	files.sort((a, b) => {
		const chunksA = a.props.path as string[] || [];
		const chunksB = b.props.path as string[] || [];
		for (let i = 0; i < Math.min(chunksA.length, chunksB.length); i++) {
			if (chunksA[i] < chunksB[i]) return -1;
			if (chunksA[i] > chunksB[i]) return 1;
		}

		if ((a.props.dir) && (b.props.dir)) return chunksA.length < chunksB.length ? -1 : 1;
		if ((a.props.dir) && !(b.props.dir)) return -1;
		if (!(a.props.dir) && (b.props.dir)) return 1;

		if (chunksA.length < chunksB.length) return 1;
		if (chunksA.length > chunksB.length) return -1;

		const f1 = (a.props.file as File);
		const f2 = (b.props.file as File);

		if (f1.input_file !== f2.input_file) return f1.input_file.localeCompare(f2.input_file);
		return a.v < b.v ? -1 : 1;
	});
}

export class FileBrowserAdapter extends BrowserAdapter {

	get etlClient() { return rp.services.get('etl') }
	get host() { return rp.host }

	getTooltip(node: NodeModel) {
		const properties = node.properties;
		if (!properties) return null;

		const file = properties.file as File;
		if (!file) return null;

		return html`
			<table style="border-collapse: collapse;">
				<tr>
					<td>${msg('Name')}:</td>
					<td>${properties.filename ?? '-'}</td>
				</tr>
				${file.description ? html`
					<tr>
						<td>${msg('Description')}:</td>
						<td>${file.description}</td>
					</tr>
				` : null}
				${file.expired > 0 ? html`
					<tr style="${styleMap({
						'background-color': 'var(--og-colortype-red-background-color)'
					})}">
						<td>${msg('Expired')}:</td>
						<td>${msg('before')} ${formatSeconds(file.expired)}</td>
					</tr>
				` : null}
				${!file.sync ? html`
					<tr>
						<td>${msg('Synchronization')}:</td>
						<td>${msg('Disabled')}</td>
					</tr>
				` : null}
				<tr>
					<td>${msg('Uploaded')}:</td>
					<td>${file.date_uploaded ? formatDate(file.date_uploaded) : '-'}</td>
				</tr>
				<tr>
					<td>${msg('Size')}:</td>
					<td>${formatBytes(file.size || 0)}</td>
				</tr>
				<tr>
					<td>${msg('Table')}:</td>
					<td>${properties.tableName}</td>
				</tr>
			</table>
		`;
	}

	async getTree(): Promise<TreeSource> {
		const treeItems: NodeSource[] = [];
		const files = await this.etlClient.get<File[]>(`files`, {
			fields: ['id_file', 'input_file', 'exists', 'expired', 'date_uploaded', 'date_modified', 'size', 'sync'],
			order: [['input_file', 'asc']]
		});

		let parents: string[] = [];
		files.forEach(file => {
			const chunks = file.input_file.split('/');
			const filename = chunks.pop();

			for (let i = 0; i < chunks.length; i++) {
				const dir = chunks[i];
				if (parents.length > i && parents[i] === dir) continue;

				treeItems.push({
					k: dir,
					v: dir,
					l: i,
					collapsed: true,
					folder: BrowserAdapter.folderOptions,
					props: {path: chunks, dir: true}
				});
			}

			treeItems.push({
				k: `etl-file-${file.id_file}`,
				v: filename,
				l: chunks.length,
				icon: 'file',
				props: {
					file: file,
					filename: filename,
					tableName: `#${file.input_file?.replace(/\.csv$/ig, '')}`,
					path: chunks,
					dir: false
				}
			});

			parents = chunks;
		});

		sortFiles(treeItems);
		return treeItems;
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		const file = node.properties?.file as File;
		const items: MenuItem[] = [];
		if (file) {
			if (this.host) items.push({key: 'open', value: msg('Open'), icon: 'book-open'});
			items.push({key: 'properties', value: msg('Properties'), icon: 'pen-to-square'});
			items.push({key: 'metadata', value: msg('Metadata'), icon: 'columns-3'});
			items.push({key: 'copyTableName', value: msg('Copy table name'), icon: 'clipboard'});
			// items.push({key: 'duplicate', value: msg('Duplicate'), icon: 'copy'});
			items.push({key: 'download', value: msg('Download'), icon: 'download'});
			items.push({key: 'upload', value: msg('Upload'), icon: 'upload'});
			items.push({key: 'delete', value: msg('Delete'), icon: 'trash', color: '#ff0000'});
		}
		return items;
	}

	async onAction(key: string, node: NodeModel) {
		const file = node.properties.file as File;
		switch (key) {
			case 'open':
				this.host.open(node.key, {title: node.value});
				break;
			case 'properties':
				this.host.openModalSidebar({
					id: `etl-fileForm-${file.id_file}`,
					caption: `#${file.id_file} ${file.input_file}`
				});
				break;
			case 'metadata':
				this.host.open(`etl-fileMetadata-${file.id_file}`, {title: node.value});
				break;
			case 'download':
				rp.host.showAlert({ type: 'info', title: msg('Export'), content: msg('Export started.') })
				await downloadEtlFile(this.etlClient, file);
				break;
			case 'upload':
				if (await uploadEtlFile(this.etlClient, file.id_file)) {
					await this.component.refresh();
					rp.host.showAlert({ type: 'success', title: msg('Upload'), content: msg('Completed successfully.') })
				}
				break;
			case 'delete':
				if (file) {
					const command = await this.host.openMenu({
						content: 'yes_no',
						text: `${msg('Are you sure you want to delete')} '#${file.id_file} ${file.input_file}'?`
					});
					if (command?.id == 'Y') {
						await this.etlClient.delete(`files/${file.id_file}`);
						this.browser.deleteNode(node);
					}
				}
				break;
			case 'copyTableName':
				const name = `#${file.input_file.substring(0, file.input_file.lastIndexOf('.'))}`;
				navigator.clipboard.writeText(name);
				rp.host.bucket.showAlert({
					type: 'info',
					content: `${msg('Copied to clipboard')}: ${name}.`
				});
				break;
		}
	}

	getOptions(): Partial<TreeOptions> {
		return this.createOptions({
			contextMenuButton: true,
			selectMode: 'none',
			expanders: false,
			customStyle: css`
				.txt {
					container-type: inline-size;
				}

				.item-text {
					flex: 1;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

				.item-date {
					display: none;
					font-size: 8px;
				}

				.item-nosync {
					flex: 0 0 auto;
					margin-left: 4px;
					font-size: 10px;
					color: var(--og-colortype-red-background-color);
				}

				@container (width > 260px) {
					.item-date {
						display: inline;
					}
				}
			`,
			nodeRenderer: (div, node) => {
				const file = node.properties?.file as File;
				if (!file) {
					div.textContent = node.value;
					return;
				}

				div.style.display = 'flex';
				div.style.flexDirection = 'row';

				render(html`
					<div class="item-text" style="${styleMap({
						color: file.expired ? 'var(--og-colortype-red-background-color)' : 'inherit'
					})}">${node.value}</div>

					${!file.sync ? html`<og-icon class="item-nosync" icon="arrows-rotate"></og-icon>` : ''}

					${file.date_uploaded ? html`
						<div class="item-date">${formatDate(file.date_uploaded)}</div>
					` : null}
				`, div);
			},
			afterRenderer: (div, node) => {
				const file = node.properties?.file as File;
				div.style.borderLeft = file
					? `3px solid var(--og-colortype-${file.exists ? 'green' : 'red'}-background-color)`
					: 'none'
			},
		});
	}

}
